/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                          WELCOME SCREEN                                   ║
 * ║              Main Hub + Onboarding Flow Controller                        ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║                                                                           ║
 * ║  This component serves TWO purposes:                                      ║
 * ║  1. Main dashboard hub with navigation buttons                            ║
 * ║  2. Orchestrates the onboarding modal flow for new users                  ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  ONBOARDING FLOW (in order):                                              ║
 * ║                                                                           ║
 * ║  1. WelcomeBackModal         → Greeting message                           ║
 * ║  2. ProfileSetupModal        → Name, phone, avatar                        ║
 * ║  3. FirstMessageModal        → "Anything you've always wanted to tell me?"║
 * ║  4. SubscriptionOnboardingModal → Plan selection + payment                ║
 * ║     └─ OR "I'll do later"    → Sets payment_deferred_at                   ║
 * ║  5. PostPaymentWelcomeModal  → Final welcome (if paid)                    ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  KEY BEHAVIORS:                                                           ║
 * ║                                                                           ║
 * ║  • FirstMessageModal is REQUIRED (cannot be skipped)                      ║
 * ║  • Message sent immediately via POST /api/subscriptions/onboarding/first-message
 * ║                                                                           ║
 * ║  • SubscriptionOnboardingModal can be skipped if user has trial           ║
 * ║  • Skip sets payment_deferred_at via POST /api/subscriptions/defer-payment║
 * ║  • Users who skip show RED in admin inbox after 7 days                    ║
 * ║                                                                           ║
 * ║  • TrialCountdownPill shows days remaining for trialing users             ║
 * ║  • Clicking pill navigates to /subscription page                          ║
 * ║                                                                           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  LAYOUT (when not in onboarding):                                         ║
 * ║  - Row 1: Text (full width)                                               ║
 * ║  - Row 2: Gallery (full width)                                            ║
 * ║  - Row 3: Account, Wallet, Vault, Newsroom (4 columns)                    ║
 * ║  - Trial pill (if trialing)                                               ║
 * ║                                                                           ║
 * ║  All styling is configurable in config/welcomeConfig.ts                   ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/features/auth'
import { useAccessLevel, clearAccessLevelCache } from '@/hooks/useAccessLevel'
import { WelcomeBackModal } from '@/components/auth/WelcomeBackModal'
import { ProfileSetupModal } from '@/components/auth/ProfileSetupModal'
import { FirstMessageModal } from '@/components/auth/FirstMessageModal'
import { YearAccessModal } from '@/components/auth/YearAccessModal'
import { PostPaymentWelcomeModal } from '@/components/auth/PostPaymentWelcomeModal'
import { useVipStatus } from '@/hooks/useVipStatus'
import { EnvelopeModal } from '@/features/mailing/EnvelopeModal'
import { useMailingAddress } from '@/features/mailing/useMailingAddress'
import { CREATOR_NAME } from '@/config/brandConfig'
import { BottomNav } from '@/components/layout/BottomNav'

// ── Hub card presentational components ──
type HubCardProps = { title: string; subtitle: string; image: string; icon: JSX.Element; onClick: () => void }

// Card photo: loads right away (these photos are at the top of the home page, so lazy loading only delays them and
// some phone browsers never start a lazy photo), and if the network drops it, it tries again (up to 3 times) so a
// card never stays empty.
function CardImage({ src, className }: { src: string; className: string }) {
  const [attempt, setAttempt] = useState(0)
  const url = attempt === 0 ? src : `${src}?retry=${attempt}`
  return (
    <img
      key={url}
      src={url}
      alt=""
      className={className}
      loading="eager"
      decoding="async"
      draggable={false}
      onError={() => {
        if (attempt < 3) window.setTimeout(() => setAttempt((a) => a + 1), 800 * (attempt + 1))
      }}
    />
  )
}

function FeatureCard({ title, subtitle, image, icon, onClick }: HubCardProps) {
  return (
    <button
      onClick={onClick}
      className="group w-full h-32 rounded-2xl bg-braydin-sand overflow-hidden flex text-left shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex-1 p-5 flex flex-col justify-center min-w-0">
        <span className="text-braydin-olive mb-2">{icon}</span>
        <h3 className="font-heading font-extrabold text-braydin-ink text-2xl leading-none">{title}</h3>
        <p className="mt-1.5 text-sm text-braydin-ink/55 truncate">{subtitle}</p>
      </div>
      <div className="relative w-32 sm:w-40 h-32 shrink-0">
        <CardImage src={image} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-braydin-sand to-transparent" />
        <span className="absolute right-3 bottom-3 text-white drop-shadow">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>
        </span>
      </div>
    </button>
  )
}

function SmallCard({ title, subtitle, image, icon, onClick }: HubCardProps) {
  return (
    <button
      onClick={onClick}
      className="group rounded-2xl bg-braydin-sand overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="relative h-24 w-full">
        <CardImage src={image} className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="p-4">
        <span className="text-braydin-olive">{icon}</span>
        <h3 className="mt-1.5 font-heading font-extrabold text-braydin-ink text-lg leading-none">{title}</h3>
        <p className="mt-1 text-xs text-braydin-ink/55">{subtitle}</p>
      </div>
    </button>
  )
}

// Small olive line icons for the cards
const cardIcons = {
  messages: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" /></svg>),
  gallery: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="1.6" /><path d="m21 15-5-5L5 21" /></svg>),
  newsroom: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h11a1 1 0 0 1 1 1v14a2 2 0 0 0 2-2V7" /><path d="M16 19a2 2 0 0 1-2 2H5a1 1 0 0 1-1-1V4" /><path d="M8 8h4M8 12h4M8 16h2" /></svg>),
  store: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>),
  vault: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="11" cy="12" r="3.4" /><path d="M11 8.6v1M11 14.4v1M7.6 12h1M14.4 12h-1" /><path d="M18 8.5v7" /></svg>),
}

export function WelcomeScreen() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [showProfileSetupModal, setShowProfileSetupModal] = useState(false)
  const [showFirstMessageModal, setShowFirstMessageModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showPostPaymentWelcome, setShowPostPaymentWelcome] = useState(false)
  // Invite token is persisted to localStorage; state setter retained for that flow.
  const [, setInviteToken] = useState<string | null>(null)
  const [showEnvelopeModal, setShowEnvelopeModal] = useState(false)
  const { hasAddress, isLoading: isMailingLoading, refresh: refreshMailing } = useMailingAddress()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isVipActive } = useVipStatus()

  // Use the access level hook to get trial days remaining
  // This is the single source of truth for subscription/trial status
  const { trialDaysRemaining, expiresAt, state, accessLevel, hasAccess } = useAccessLevel()

  // Get grace days from accessLevel if available
  const graceDaysRemaining = accessLevel?.graceDaysRemaining ?? null

  // Calculate days remaining from multiple sources (trial, grace, or expiresAt)
  const calculateDaysRemaining = (): number | null => {
    // Priority 1: Explicit trial days from backend
    if (trialDaysRemaining !== null && trialDaysRemaining > 0) {
      return trialDaysRemaining
    }

    // Priority 2: Grace period days
    if (graceDaysRemaining !== null && graceDaysRemaining > 0) {
      return graceDaysRemaining
    }

    // Priority 3: Calculate from expiresAt for any expiring access
    if (expiresAt) {
      const expires = new Date(expiresAt)
      const now = new Date()
      const diffMs = expires.getTime() - now.getTime()
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      if (days > 0) {
        return days
      }
    }

    return null
  }

  const daysRemaining = calculateDaysRemaining()

  // Auto-open envelope modal when redirected from /vip with openMailbox param
  useEffect(() => {
    if (searchParams.get('openMailbox') === 'true' && !hasAddress && !isMailingLoading) {
      setShowEnvelopeModal(true)
      searchParams.delete('openMailbox')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, hasAddress, isMailingLoading, setSearchParams])

  // Ref to track if onboarding has started - prevents loop when user object isn't updated
  const onboardingStartedRef = useRef(false)

  // Check if user already has a subscription OR premium access
  const checkIfNeedsSubscription = async () => {
    if (!user?.id) return

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Check if user already has active subscription - if so, skip subscription modal
      const accessLevelResponse = await fetch(`${API_URL}/api/subscriptions/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (accessLevelResponse.ok) {
        const accessLevel = await accessLevelResponse.json()
        // Skip if user has any valid access (active, trialing, canceled-but-period-active, grace, etc.)
        if (accessLevel.hasAccess) {
          return
        }
      }

      // CRITICAL: Check if user has pending invite FIRST before checking subscription
      // This includes customer_benefits users who get auto-generated invites
      const inviteResponse = await fetch(`${API_URL}/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(user.email)}`)
      if (inviteResponse.ok) {
        const inviteData = await inviteResponse.json()
        if (inviteData.hasInvite && inviteData.invite?.token) {
          // Store invite token for subscription modal
          localStorage.setItem('pending_invite_token', inviteData.invite.token)
          setInviteToken(inviteData.invite.token)

          // If needsWelcome flag was set, they need to complete onboarding FIRST
          // Don't show subscription modal yet - handleProfileSetupComplete will handle it
          const needsWelcome = localStorage.getItem('needsWelcome')
          if (needsWelcome === 'true') {
            return
          }

          // Otherwise, show subscription modal with their invite
          setShowSubscriptionModal(true)
          return
        }
      }

      // Check both subscription AND access status
      const [subResponse, accessResponse] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscriptions/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      const subData = subResponse.ok ? await subResponse.json() : null
      const accessData = accessResponse.ok ? await accessResponse.json() : null

      // Note: Trial days remaining is now handled by useAccessLevel hook
      // No need to manually calculate here

      // Show subscription modal if:
      // 1. No subscription at all, OR
      // 2. Subscription exists but is in a state that requires re-subscription
      const needsSubscription = !subData?.subscription ||
        ['incomplete', 'incomplete_expired', 'canceled', 'unpaid'].includes(subData?.subscription?.status)

      if (needsSubscription && !accessData?.hasAccess) {
        setShowSubscriptionModal(true)
      } else {
        // Clear any pending flags
        localStorage.removeItem('pending_invite_token')
        localStorage.removeItem('has_pending_subscription')
      }
    } catch (error) {
      console.error('[WelcomeScreen] Error checking subscription:', error)
    }
  }

  // Check if user needs welcome flow on mount
  useEffect(() => {
    const needsWelcome = localStorage.getItem('needsWelcome')
    const pendingInvite = localStorage.getItem('pending_invite_token')
    if (pendingInvite) {
      setInviteToken(pendingInvite)
    }

    // IMPORTANT: Show onboarding FIRST if needed
    // Check BOTH localStorage flag AND actual user profile data
    // BUT only if we haven't already started onboarding in this session (prevents loop)
    // AND only if onboarding hasn't been completed (prevents post-payment loop)
    // AND only if user doesn't already have access (active sub = already onboarded)
    const onboardingComplete = localStorage.getItem('onboarding_complete') === 'true'
    const userNeedsOnboarding = !onboardingStartedRef.current && !onboardingComplete && !hasAccess && (
      needsWelcome === 'true' ||
      (user && (!user.firstName && !(user as any).first_name))
    )

    if (userNeedsOnboarding) {
      onboardingStartedRef.current = true // Mark that onboarding has started - prevents re-triggering
      setShowWelcomeModal(true)
      // Clear the flag so it doesn't show again on refresh
      localStorage.removeItem('needsWelcome')
      // DON'T check subscription - onboarding will handle showing subscription modal after completion
      return // IMPORTANT: Don't run any other checks
    }

    // If onboarding modals are open, don't check subscription
    if (showWelcomeModal || showProfileSetupModal || showFirstMessageModal) {
      return
    }

    // If user has pending invite, check if they're a returning user or new user
    // This handles both: manually invited users AND customer_benefits users who get auto-invite
    if (pendingInvite && user?.id) {
      const hasCompletedProfile = user.firstName || (user as any).first_name

      if (!hasCompletedProfile) {
        // New user who hasn't set up profile yet - wait for profile setup
        // Don't call checkIfNeedsSubscription() yet - handleProfileSetupComplete will handle it
        return
      } else {
        // Returning user with stale invite token - clear it and check subscription
        // This fixes the bug where users get stuck in redirect loop when their subscription expires
        localStorage.removeItem('pending_invite_token')
        // Call subscription check directly since local pendingInvite var still has old value
        checkIfNeedsSubscription()
        return
      }
    }

    // ONLY check subscription if:
    // 1. User is loaded
    // 2. No onboarding in progress (neither modal is open)
    // 3. No pending invite
    // 4. needsWelcome flag is not set
    if (user?.id && !needsWelcome && !pendingInvite && !showWelcomeModal && !showProfileSetupModal && !showFirstMessageModal) {
      // User is logged in AND already completed onboarding - check if they need subscription
      checkIfNeedsSubscription()
    } else {
      // no-op
    }
  }, [user, showWelcomeModal, showProfileSetupModal, showFirstMessageModal])

  // Handle welcome modal continue
  const handleWelcomeContinue = () => {
    setShowWelcomeModal(false)
    setShowProfileSetupModal(true)
  }

  // Handle profile setup complete
  const handleProfileSetupComplete = async () => {
    setShowProfileSetupModal(false)

    // Check if user already has a conversation/message - if so, skip FirstMessageModal
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      const response = await fetch(`${API_URL}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        const hasExistingConversation = data.conversations && data.conversations.length > 0

        if (hasExistingConversation) {
          // Go directly to subscription modal
          showSubscriptionModalAfterOnboarding()
          return
        }
      }
    } catch (err) {
      console.warn('[WelcomeScreen] Could not check for existing conversations:', err)
      // Continue with first message modal on error
    }

    // Show FirstMessageModal BEFORE subscription modal
    // This is part of the onboarding flow: Profile -> First Message -> Subscription
    setShowFirstMessageModal(true)
  }

  // Helper function to show subscription modal after onboarding
  // Checks if user already has an active subscription first to avoid duplicate prompts
  const showSubscriptionModalAfterOnboarding = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Check if user already has active subscription - skip modal if so
      const accessLevelResponse = await fetch(`${API_URL}/api/subscriptions/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (accessLevelResponse.ok) {
        const accessData = await accessLevelResponse.json()
        if (accessData.hasAccess && (accessData.state === 'ACTIVE' || accessData.state === 'TRIALING')) {
          // Already subscribed - skip subscription modal, go straight to post-payment welcome
          localStorage.removeItem('pending_invite_token')
          setShowPostPaymentWelcome(true)
          return
        }
      }
    } catch (err) {
      console.warn('[WelcomeScreen] Could not check subscription status:', err)
      // Continue to show modal on error - better to show it than skip it
    }

    const pendingInvite = localStorage.getItem('pending_invite_token')
    if (pendingInvite) {
      setInviteToken(pendingInvite)
    }
    setShowSubscriptionModal(true)
  }

  // Handle first message submission
  const handleFirstMessageSubmit = async (message: string) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const token = localStorage.getItem('auth_token')

      // Save first message to backend
      const response = await fetch(`${API_URL}/api/subscriptions/onboarding/first-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message })
      })

      if (response.ok) {
        // no-op
      } else {
        console.warn('[WelcomeScreen] Failed to save first message:', await response.text())
      }
    } catch (err) {
      console.error('[WelcomeScreen] Failed to save first message:', err)
      // Continue anyway - don't block onboarding
    }

    setShowFirstMessageModal(false)

    // ALWAYS show subscription modal after first message (onboarding requirement)
    // Users with invite get their trial, users without must pay to enter
    showSubscriptionModalAfterOnboarding()
  }

  // Handle subscription setup complete
  const handleSubscriptionComplete = () => {
    setShowSubscriptionModal(false)
    setInviteToken(null)
    // Mark onboarding as fully complete so we never loop back
    localStorage.setItem('onboarding_complete', 'true')
    localStorage.removeItem('needsWelcome')
    // Show post-payment welcome modal
    setShowPostPaymentWelcome(true)
  }


  // Handle post-payment welcome complete
  const handlePostPaymentWelcomeComplete = () => {
    setShowPostPaymentWelcome(false)
    // Clear cached access level so ProtectedRoute sees the new subscription
    clearAccessLevelCache()
    // Force full page reload to refetch everything fresh
    window.location.href = '/messaging'
  }

  // Logged-in fan's first name for the "WELCOME BACK," greeting
  const firstName = ((user?.firstName || (user as any)?.first_name || '') as string).trim()
  const displayName = (firstName || 'friend').toUpperCase()

  return (
    <>
      {/* Welcome Flow Modals */}
      <WelcomeBackModal
        isOpen={showWelcomeModal}
        onContinue={handleWelcomeContinue}
      />

      {user && (
        <ProfileSetupModal
          isOpen={showProfileSetupModal}
          user={user}
          onComplete={handleProfileSetupComplete}
        />
      )}

      {/* First Message Modal - Shows after profile setup, before subscription */}
      <FirstMessageModal
        isOpen={showFirstMessageModal}
        onSubmit={handleFirstMessageSubmit}
      />

      {user && (
        <YearAccessModal
          isOpen={showSubscriptionModal}
          onComplete={handleSubscriptionComplete}
        />
      )}

      {/* Post-Payment Welcome Modal */}
      <PostPaymentWelcomeModal
        isOpen={showPostPaymentWelcome}
        onContinue={handlePostPaymentWelcomeComplete}
      />

      {/* Envelope Modal for mailing address collection */}
      <EnvelopeModal
        isOpen={showEnvelopeModal}
        onClose={() => setShowEnvelopeModal(false)}
        onAddressSaved={refreshMailing}
      />

      {/* Main Welcome Screen */}
      <div className="relative min-h-screen braydin-app-bg pb-28">
        <div className="w-full max-w-md mx-auto px-5 pt-10 animate-fade-in">

          {/* Welcome header */}
          <div className="mb-6">
            <p className="font-heading tracking-[0.28em] text-[11px] text-braydin-sage mb-1.5">WELCOME BACK,</p>
            <h1 className="font-heading font-extrabold text-braydin-ink text-5xl leading-none">{displayName}</h1>
            <div className="mt-3 h-1 w-14 rounded-full bg-braydin-olive" />
          </div>

          {/* Cards */}
          <div className="space-y-4">
            <FeatureCard
              title="MESSAGES"
              subtitle={`Chat with ${CREATOR_NAME}`}
              image="/braydin/card-messages-bw.jpg"
              icon={cardIcons.messages}
              onClick={() => navigate('/messages')}
            />
            <FeatureCard
              title="GALLERY"
              subtitle="Photos, videos, and memories"
              image="/braydin/card-gallery.jpg"
              icon={cardIcons.gallery}
              onClick={() => navigate('/gallery')}
            />
            <div className="grid grid-cols-2 gap-4">
              <SmallCard
                title="NEWSROOM"
                subtitle="Notes and updates"
                image="/braydin/card-newsroom-bw.jpg"
                icon={cardIcons.newsroom}
                onClick={() => navigate('/newsroom')}
              />
              <SmallCard
                title="VAULT"
                subtitle="Your purchased content"
                image="/braydin/card-vault-bw.jpg"
                icon={cardIcons.vault}
                onClick={() => navigate('/vault')}
              />
            </div>

            {/* Latest update */}
            <button
              onClick={() => navigate('/newsroom')}
              className="w-full rounded-2xl bg-braydin-sand p-4 flex items-center gap-4 text-left shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex-1 min-w-0">
                <p className="font-heading tracking-[0.22em] text-[10px] text-braydin-sage mb-1">LATEST UPDATE</p>
                <h4 className="font-heading font-bold text-braydin-ink text-lg leading-tight truncate">South Dakota Trip</h4>
              </div>
              <div className="shrink-0 w-24 h-24 rounded-lg overflow-hidden rotate-2 shadow-md ring-4 ring-white">
                <CardImage src="/braydin/update-rushmore.jpg" className="w-full h-full object-cover" />
              </div>
            </button>

            {/* VIP mail affordance (only for VIP mailing subscribers) */}
            {isVipActive && (
              <button
                onClick={() => navigate('/mail')}
                className="w-full rounded-2xl bg-braydin-sand2 border border-braydin-taupe/40 p-4 flex items-center justify-center gap-2 text-braydin-ink/80 hover:bg-braydin-sand transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
                <span className="font-heading tracking-[0.15em] text-xs">VIP MAIL</span>
              </button>
            )}

            {/* Access-ended banner */}
            {(daysRemaining === null || daysRemaining <= 0) && state !== 'ACTIVE' && expiresAt && new Date(expiresAt) < new Date() && (
              <div className="flex justify-center pt-1">
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium border border-red-200 hover:bg-red-200 transition-colors"
                >
                  Access ended — Subscribe now
                </button>
              </div>
            )}
          </div>
        </div>

        <BottomNav />
      </div>
    </>
  )
}
